import { plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { Copy, Pencil, Trash2 } from 'lucide-react';
import { MediaManagerTemplateGroup } from 'modules/manager/domain/interfaces/media-manager-template-group';
import React from 'react';
import { theme } from 'styles/theme/theme';
import { UDButton, UDText } from 'ud-ui/components';
import EmptyButton from 'ud-ui/components/empty-button';
import UDTippy from 'ud-ui/components/tippy';
import { PRESET_CARD_ACTIONS_GAP, PRESET_CARD_CLASS } from './preset-card-metrics';

interface IProps {
  group: MediaManagerTemplateGroup;
  canWrite?: boolean;
  canDuplicate?: boolean;
  isDuplicating?: boolean;
  onOpen: (group: MediaManagerTemplateGroup) => void;
  onRename?: (group: MediaManagerTemplateGroup) => void;
  onDelete?: (group: MediaManagerTemplateGroup) => void;
  onDuplicate?: (group: MediaManagerTemplateGroup) => void;
}

function formatUpdatedAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('ru-RU');
}

function MediaManagerTemplateGroupCard(props: IProps) {
  const { group, canWrite, canDuplicate, isDuplicating, onOpen, onRename, onDelete, onDuplicate } = props;
  const { t } = useLingui();

  return (
    <div className={PRESET_CARD_CLASS}>
      <div className="flex min-h-[88px] min-w-0 flex-col justify-between">
        <UDText variant="body-2" className="truncate font-bold">
          {group.name}
        </UDText>
        <div className="flex flex-col gap-1">
          <UDText variant="body-3" className="text-gray-400">
            {plural(group.templatesCount, { one: '# шаблон', few: '# шаблона', many: '# шаблонов', other: '# шаблонов' })}
          </UDText>
          {group.updatedAt && (
            <UDText variant="body-3" className="text-gray-400">
              {t`изменён ${formatUpdatedAt(group.updatedAt)}`}
            </UDText>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <UDButton variant="primary" onClick={() => onOpen(group)}>
          <Trans>Открыть</Trans>
        </UDButton>
        <div className="flex items-center" style={{ gap: PRESET_CARD_ACTIONS_GAP }}>
          {canDuplicate && onDuplicate && (
            <UDTippy content={t`Дублировать группу в организацию`} placement={'top'}>
              <EmptyButton
                icon={<Copy size={20} />}
                disabled={isDuplicating}
                loading={isDuplicating}
                loaderSize={20}
                aria-label={t`Дублировать группу в организацию`}
                onClick={() => onDuplicate(group)}
              />
            </UDTippy>
          )}
          {canWrite && onRename && (
            <UDTippy content={t`Переименовать`} placement={'top'}>
              <EmptyButton icon={<Pencil size={20} />} aria-label={t`Переименовать`} onClick={() => onRename(group)} />
            </UDTippy>
          )}
          {canWrite && onDelete && (
            <UDTippy content={t`Удалить`} placement={'top'}>
              <EmptyButton color={theme.colors.error} icon={<Trash2 size={20} />} aria-label={t`Удалить`} onClick={() => onDelete(group)} />
            </UDTippy>
          )}
        </div>
      </div>
    </div>
  );
}

export default MediaManagerTemplateGroupCard;
